import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import debounce from "debounce-promise";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import CareProfileInput from "@components/fields/CareProfileInput";
import Select from "@components/fields/Select";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import CareProfileFormCondition from "@components/forms/careProfile/CareProfileFormCondition";
import Form from "@components/forms/Form";
import {
  CareProfileEmotionalConcernEnum,
  CareProfileFieldNameEnum,
  CareProfileFieldScopeEnum,
  CareProfileReligionEnum,
  CareProfileSenseOfFulfillmentEnum,
  CareProfileSocialSatisfactionEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  GetCareProfileFieldRulesQuery,
  GetJourneyWithCareProfileQuery
} from "@typing/Generated";
import { filterForDirtyValidValues, handleMutationResponse } from "@utils/formUtils";
import {
  optionsForEmotionalConcern,
  optionsForReligion,
  optionsForSenseOfFulfillment,
  optionsForSocialSatisfaction
} from "@utils/optionUtils";

type CareProfileFieldRules = GetCareProfileFieldRulesQuery["careProfileFieldRules"];

type Props = {
  careProfileFieldRules: CareProfileFieldRules;
  journey: GetJourneyWithCareProfileQuery["journey"];
};

type FormData = {
  emotionalConcerns?: CareProfileEmotionalConcernEnum[];
  emotionalConcernsOther?: string | null;
  religion?: CareProfileReligionEnum | null;
  religionOther?: string | null;
  senseOfFulfillment?: CareProfileSenseOfFulfillmentEnum | null;
  socialSatisfaction?: CareProfileSocialSatisfactionEnum | null;
};

const CareProfileSocialForm = ({ careProfileFieldRules, journey }: Props) => {
  const intl = useIntl();

  const careProfile = journey.careProfile;

  const defaultValues: FormData = {
    emotionalConcerns: careProfile?.emotionalConcerns?.emotionalConcernsOptions ?? [],
    emotionalConcernsOther: careProfile?.emotionalConcerns?.other ?? null,
    religion: careProfile.religion?.religionOption ?? null,
    religionOther: careProfile.religion?.other ?? null,
    senseOfFulfillment: careProfile?.senseOfFulfillment ?? null,
    socialSatisfaction: careProfile?.socialSatisfaction ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      emotionalConcerns: yup.array().of(yup.string().oneOf(Object.values(CareProfileEmotionalConcernEnum)).defined()),
      emotionalConcernsOther: yup.string().nullable(),
      religion: yup.string().nullable().oneOf(Object.values(CareProfileReligionEnum)),
      religionOther: yup.string().nullable(),
      senseOfFulfillment: yup.string().nullable().oneOf(Object.values(CareProfileSenseOfFulfillmentEnum)),
      socialSatisfaction: yup.string().nullable().oneOf(Object.values(CareProfileSocialSatisfactionEnum))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    const dirtyValidValues = filterForDirtyValidValues(form);
    const fieldsToBeSubmitted = Object.keys(dirtyValidValues);
    const {
      emotionalConcerns,
      emotionalConcernsOther,
      religion,
      religionOther,
      ...dirtyValidCareProfileUpdateVariables
    } = dirtyValidValues;
    const careProfileUpdateVariables: CareProfileUpdateMutationVariables = {
      id: journey.careProfile.id,
      ...dirtyValidCareProfileUpdateVariables
    };

    if (fieldsToBeSubmitted.includes("emotionalConcerns")) {
      careProfileUpdateVariables.emotionalConcerns = {
        emotionalConcernsOptions: emotionalConcerns,
        other: emotionalConcerns?.includes(CareProfileEmotionalConcernEnum.OTHER) ? emotionalConcernsOther : null
      };
    }

    if (fieldsToBeSubmitted.includes("religion")) {
      careProfileUpdateVariables.religion = {
        other: religion === CareProfileReligionEnum.OTHER ? religionOther : null,
        religionOption: religion
      };
    }

    if (Object.keys(careProfileUpdateVariables).length > 0) {
      setBaseErrors([]);
      return updateCareProfile({ variables: careProfileUpdateVariables }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.careProfileUpdate,
          intl,
          onError: () => {},
          onSuccess: () => {},
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.careProfile"
        });
      });
    }
  }, [form, intl, journey.careProfile.id, updateCareProfile]);

  useEffect(() => {
    const debouncedSubmit = debounce(submitCareProfile, 2000);
    const subscription = form.watch(() => form.handleSubmit(debouncedSubmit, debouncedSubmit)());
    return () => subscription.unsubscribe();
  }, [form, submitCareProfile]);

  const emotionalConcernOptions = optionsForEmotionalConcern(intl);
  const religionOptions = optionsForReligion(intl);
  const senseOfFulfillmentOptions = optionsForSenseOfFulfillment(intl);
  const socialSatisfactionOptions = optionsForSocialSatisfaction(intl);

  return (
    <Form hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <div className={styles.formSectionRow}>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.EMOTIONAL_CONCERNS}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="emotionalConcerns"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.emotionalConcerns}
              isMulti
              label={intl.formatMessage({ id: "forms.careProfileNew.emotionalConcernsLabel" })}
              mutuallyExclusiveValues={[CareProfileEmotionalConcernEnum.NONE]}
              name="emotionalConcerns"
              options={emotionalConcernOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.EMOTIONAL_CONCERNS_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="emotionalConcernsOther"
            otherField="emotionalConcerns"
            otherValue={CareProfileEmotionalConcernEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.emotionalConcernsOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.emotionalConcernsOtherLabel" })}
              register={form.register("emotionalConcernsOther")}
              spellcheck
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.RELIGION}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="religion"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.religion}
              label={intl.formatMessage({ id: "forms.careProfileNew.religionLabel" })}
              name="religion"
              options={religionOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.RELIGION_OTHER}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="religionOther"
            otherField="religion"
            otherValue={CareProfileReligionEnum.OTHER}
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <CareProfileInput<FormData>
              error={form.formState.errors.religionOther}
              label={intl.formatMessage({ id: "forms.careProfileNew.religionOtherLabel" })}
              register={form.register("religionOther")}
              spellcheck
            />
          </CareProfileFormCondition>
        </div>
        <div className={styles.formControlsColumn}>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.SENSE_OF_FULFILLMENT}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="senseOfFulfillment"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.senseOfFulfillment}
              label={intl.formatMessage({ id: "forms.careProfileNew.senseOfFulfillmentLabel" })}
              name="senseOfFulfillment"
              options={senseOfFulfillmentOptions}
            />
          </CareProfileFormCondition>
          <CareProfileFormCondition<FormData>
            careProfile={careProfile}
            fieldName={CareProfileFieldNameEnum.SOCIAL_SATISFACTION}
            fieldRules={careProfileFieldRules}
            form={form}
            formField="socialSatisfaction"
            scope={CareProfileFieldScopeEnum.CARE_PROFILE}
          >
            <Select<FormData>
              clearable
              control={form.control}
              error={form.formState.errors.socialSatisfaction}
              label={intl.formatMessage({ id: "forms.careProfileNew.socialSatisfactionLabel" })}
              name="socialSatisfaction"
              options={socialSatisfactionOptions}
            />
          </CareProfileFormCondition>
        </div>
      </div>
    </Form>
  );
};

export default CareProfileSocialForm;
